import { useCallback } from 'react';
import { getHealth } from '../api/client';
import { usePolling } from '../api/hooks';
import type { Health } from '../types';
import { Badge, type Tone } from './StatusBadge';

const HEALTHY_WORDS = new Set(['ok', 'up', 'ready', 'connected', 'healthy', 'true']);

interface Dependency {
  name: string;
  label: string;
  ok: boolean | null;
}

/** The contract does not fix whether these are booleans or strings, so accept both. */
function readDependency(name: string, value: string | boolean | null | undefined): Dependency {
  if (value === undefined || value === null) return { name, label: 'unknown', ok: null };
  if (typeof value === 'boolean') return { name, label: value ? 'up' : 'down', ok: value };
  return { name, label: value, ok: HEALTHY_WORDS.has(value.trim().toLowerCase()) };
}

function summarise(health: Health): { tone: Tone; glyph: string; label: string; title: string } {
  const dependencies = [readDependency('mongo', health.mongo), readDependency('redis', health.redis)];
  const statusOk = HEALTHY_WORDS.has(String(health.status ?? '').trim().toLowerCase());
  const anyDown = dependencies.some((dependency) => dependency.ok === false);

  const title = dependencies.map((dependency) => `${dependency.name}: ${dependency.label}`).join(' · ');

  if (anyDown || !statusOk) {
    return { tone: 'warning', glyph: '◐', label: 'Degraded', title };
  }
  return { tone: 'success', glyph: '●', label: 'Operational', title };
}

export function HealthPill() {
  const fetcher = useCallback((signal: AbortSignal) => getHealth(signal), []);
  const { data, error, loading } = usePolling(fetcher, 10_000);

  if (loading && data === null && error === null) {
    return <Badge tone="neutral" glyph="◌" label="Checking…" size="sm" />;
  }

  if (error !== null && data === null) {
    return (
      <Badge
        tone="danger"
        glyph="✕"
        label={error.isUnreachable ? 'API unreachable' : 'API error'}
        size="sm"
        title={error.detail ?? error.message}
      />
    );
  }

  if (data === null) {
    return <Badge tone="neutral" glyph="?" label="Unknown" size="sm" />;
  }

  const summary = summarise(data);
  return <Badge tone={summary.tone} glyph={summary.glyph} label={summary.label} size="sm" title={summary.title} />;
}
